import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { translationService } from "../../services/translation/TranslationService";
import { ocrTsToSeconds, secondsToOcrTimestamp } from "./playbackSync";
import { parseVideoText } from "./videoTextParser";
import { observationTextFromFields } from "./analysisObservationText";
import {
  CONDITION_RATING_OPTIONS,
  ConditionRating,
  DEFAULT_DETECTION_STATUS,
  DETECTION_STATUS_OPTIONS,
  DetectionStatus,
  RISK_RATING_OPTIONS,
  RiskRating,
} from "./detectionAssessment";

const t = (key: string) => translationService.translate(key);

// The editable shape of a verified detection (a subset of DetectionModel the user
// can change in the comparison). timestamp/frameUrl are carried through unchanged.
export interface EditableDetection {
  main_Code: string;
  distance: number | "";
  timestamp: string;
  text: string;
  c1: string;
  c2: string;
  q1: string;
  q2: string;
  sa: string;
  postionFrom: string;
  positionTo: string;
  frameUrl: string;
  // --- The reviewer's assessment of THIS detection. Saved to the approved detection list,
  // never back into ocr.json. Empty rating = not assessed yet, which is not the same as a
  // good one, so neither has a default beyond "unset".
  detectionStatus: DetectionStatus;
  conditionRating: ConditionRating | "";
  riskRating: RiskRating | "";
}

const empty: EditableDetection = {
  main_Code: "",
  // Empty, not 0. A prefilled 0 is a valid-looking station that nobody chose: it saved rows at
  // 0.00 m, and since identity is station + main code, two such rows collided on one key.
  distance: "",
  timestamp: "",
  text: "",
  c1: "",
  c2: "",
  q1: "",
  q2: "",
  sa: "",
  postionFrom: "",
  positionTo: "",
  frameUrl: "",
  detectionStatus: DEFAULT_DETECTION_STATUS,
  conditionRating: "",
  riskRating: "",
};

interface DetectionEditDialogProps {
  open: boolean;
  title: string;
  /**
   * Offer "write the text from the fields" alongside the existing "read the fields from the
   * text".
   *
   * Opt-in because the two sides do not need it equally: an OCR row already HAS the van's
   * overlay text — that is where its coding came from — so generating a sentence from the boxes
   * would offer to replace the source with a paraphrase of itself. A video-analysis row has no
   * text at all, which is the gap this fills.
   */
  allowTextFromFields?: boolean;
  initial?: Partial<EditableDetection> | null;
  codeOptions: string[];
  /**
   * The station at a video position, from the analysis run's time -> metre track. When given,
   * the distance is filled in from the time -- on open if it is empty, and whenever the time is
   * changed -- unless the reviewer has typed a distance of their own.
   */
  distanceForTime?: (seconds: number) => number | undefined;
  onSave: (detection: EditableDetection) => void;
  onCancel: () => void;
}

/**
 * Seconds from an hh:mm:ss (or mm:ss) entry, or null when it is not a time.
 *
 * Null rather than 0 on purpose: 0 is a valid moment, so a parse failure has to be reportable as
 * a failure instead of quietly meaning "the start of the video".
 */
const secondsFromClock = (value: string): number | null => {
  const parts = value.trim().split(":");
  if (parts.length < 2 || parts.length > 3) return null;
  if (parts.some((p) => p.trim() === "" || !/^\d+$/.test(p.trim()))) return null;

  const numbers = parts.map((p) => Number(p));
  const [h, m, s] = parts.length === 3 ? numbers : [0, numbers[0], numbers[1]];
  if (m > 59 || s > 59) return null;
  return h * 3600 + m * 60 + s;
};

/** hh:mm:ss for the input, from the ISO timestamp the row stores. */
const clockFromTimestamp = (timestamp: string): string => {
  if (!timestamp) return "";
  const seconds = Math.max(0, Math.round(ocrTsToSeconds(timestamp)));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
};

const DetectionEditDialog: React.FC<DetectionEditDialogProps> = ({
  open,
  title,
  initial,
  codeOptions,
  onSave,
  onCancel,
  allowTextFromFields,
  distanceForTime,
}) => {
  const [d, setD] = useState<EditableDetection>(empty);
  // The raw text of the time box, so a half-typed "00:1" is not thrown away mid-keystroke by the
  // round trip through the ISO timestamp.
  const [timeText, setTimeText] = useState("");
  // Shown only when the text yielded nothing, so a press that did nothing does not read as
  // a broken button.
  const [parseHint, setParseHint] = useState(false);

  // The last observation text this dialog wrote itself. It is what tells an automatic refresh
  // apart from something the reviewer typed: a box still holding exactly this may be rewritten,
  // anything else is theirs and is left alone.
  const generated = useRef<string | null>(null);

  // The last distance this dialog filled in from the time, for the same reason: a box still
  // holding it may follow the time, anything else was typed and is left alone.
  const autoDistance = useRef<number | null>(null);

  /** The track's station at `seconds`, to two decimals, or undefined when it has none. */
  const stationAt = useCallback(
    (seconds: number): number | undefined => {
      const meters = distanceForTime?.(seconds);
      return meters === undefined || Number.isNaN(meters)
        ? undefined
        : Math.round(meters * 100) / 100;
    },
    [distanceForTime]
  );

  const textFromFields = useCallback(
    (from: EditableDetection) =>
      observationTextFromFields({
        mainCode: from.main_Code,
        c1: from.c1,
        c2: from.c2,
        q1: from.q1,
        q2: from.q2,
        sa: from.sa,
        positionFrom: from.postionFrom,
        positionTo: from.positionTo,
        distance: from.distance,
        timeSeconds: from.timestamp ? ocrTsToSeconds(from.timestamp) : undefined,
      }),
    []
  );

  useEffect(() => {
    if (!open) return;
    const next = { ...empty, ...(initial ?? {}) };
    // An empty station with a known time: read it off the track rather than make the reviewer
    // look it up on the video's counter.
    autoDistance.current = null;
    if (String(next.distance).trim() === "" && next.timestamp) {
      const station = stationAt(ocrTsToSeconds(next.timestamp));
      if (station !== undefined) {
        next.distance = station;
        autoDistance.current = station;
      }
    }
    setD(next);
    setTimeText(clockFromTimestamp(next.timestamp));
    setParseHint(false);
    // A row approved from the checkbox already carries the text these very fields produce, so
    // seeding the marker with it recognises that note as machine-written and keeps it in step
    // as the codes are corrected. A reviewer's own wording will not match and stays untouched.
    generated.current = textFromFields(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial, textFromFields]);

  /**
   * Keep the observation text in step with the coding, without ever overwriting the reviewer.
   *
   * The text used to appear only when the button below was pressed, so a damage selected from
   * the list showed an empty box until someone thought to ask for one -- and a code corrected
   * afterwards left the text describing the damage as it was first understood. It is written
   * here whenever the coded fields change and the box is either empty or still holds this
   * dialog's own last output.
   */
  useEffect(() => {
    if (!open) return;
    setD((prev) => {
      const next = textFromFields(prev);
      const untouched = prev.text.trim() === "" || prev.text === generated.current;
      if (!untouched || next === prev.text) return prev;
      generated.current = next;
      return { ...prev, text: next };
    });
  }, [
    open,
    textFromFields,
    d.main_Code,
    d.c1,
    d.c2,
    d.q1,
    d.q2,
    d.sa,
    d.postionFrom,
    d.positionTo,
    d.distance,
    d.timestamp,
  ]);

  const set = (k: keyof EditableDetection, v: any) => setD((prev) => ({ ...prev, [k]: v }));

  // The coding is already in the overlay text the van burned into the video, so it is read
  // from there instead of being retyped into eight boxes. Only fields the text actually
  // spells out are written; the rest keep whatever the reviewer put in them.
  const fillFromText = () => {
    const parsed = parseVideoText(d.text, { knownCodes: codeOptions });
    setParseHint(Object.keys(parsed).length === 0);
    setD((prev) => ({ ...prev, ...parsed }));
  };

  // The other direction: state in words what the boxes now say. Used after correcting a code or
  // a quantification, so the observation text the documents carry describes the corrected row
  // rather than whatever it said before. Overwrites the text on purpose — it is the only reason
  // to press it — which is why it is a button and not something that happens on every edit.
  const fillTextFromFields = () => {
    setParseHint(false);
    setD((prev) => {
      const next = textFromFields(prev);
      generated.current = next;
      return { ...prev, text: next };
    });
  };
  // Station 0 is legitimate (the pipe start), so the rule is "entered", not "non-zero".
  const distanceEntered = String(d.distance).trim() !== "";
  const valid =
    d.main_Code.trim() !== "" &&
    distanceEntered &&
    !Number.isNaN(Number(d.distance)) &&
    Number(d.distance) >= 0;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {d.frameUrl ? (
          <Box
            component="img"
            src={d.frameUrl}
            alt="frame"
            sx={{
              display: "block",
              width: "100%",
              maxHeight: 240,
              objectFit: "contain",
              borderRadius: 1,
              border: "1px solid rgba(0,0,0,0.15)",
              mb: 2,
              mt: 1,
            }}
          />
        ) : null}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
          <Autocomplete
            freeSolo
            options={codeOptions}
            value={d.main_Code}
            onInputChange={(_e, v) => set("main_Code", v ?? "")}
            renderInput={(params) => <TextField {...params} label="Main code" required />}
          />
          <TextField
            label="Distance (m)"
            type="number"
            required
            value={d.distance}
            error={!distanceEntered}
            onChange={(e) =>
              set("distance", e.target.value === "" ? "" : Number(e.target.value))
            }
            inputProps={{ step: 0.01, min: 0 }}
          />
          {/* Editable, and it was not before: the value was only ever taken from the playhead at
              the moment Add was pressed, so a reviewer who noticed the wrong moment had no way to
              correct it. Held as an ISO timestamp because that is what ocr.json uses, but typed
              as hh:mm:ss, which is how the video reads it. */}
          <TextField
            label="Time (hh:mm:ss)"
            value={timeText}
            placeholder="00:00:00"
            error={timeText !== "" && secondsFromClock(timeText) === null}
            onChange={(e) => {
              setTimeText(e.target.value);
              const seconds = secondsFromClock(e.target.value);
              if (seconds === null) return;
              set("timestamp", secondsToOcrTimestamp(seconds));

              // Keep the station in step with the time while it is still the one this dialog
              // filled in (or empty). A distance the reviewer typed is theirs.
              const station = stationAt(seconds);
              setD((prev) => {
                const untouched =
                  String(prev.distance).trim() === "" || prev.distance === autoDistance.current;
                if (!untouched || station === undefined) return prev;
                autoDistance.current = station;
                return { ...prev, distance: station };
              });
            }}
          />
          <TextField label="C1" value={d.c1} onChange={(e) => set("c1", e.target.value)} />
          <TextField label="C2" value={d.c2} onChange={(e) => set("c2", e.target.value)} />
          <TextField label="SA" value={d.sa} onChange={(e) => set("sa", e.target.value)} />
          <TextField label="Q1" value={d.q1} onChange={(e) => set("q1", e.target.value)} />
          <TextField label="Q2" value={d.q2} onChange={(e) => set("q2", e.target.value)} />
          <TextField
            label="Position from"
            value={d.postionFrom}
            onChange={(e) => set("postionFrom", e.target.value)}
          />
          <TextField
            label="Position to"
            value={d.positionTo}
            onChange={(e) => set("positionTo", e.target.value)}
          />
          <Box
            sx={{ gridColumn: "1 / -1", display: "flex", gap: 1, alignItems: "flex-start" }}
          >
            <TextField
              label="Text"
              value={d.text}
              onChange={(e) => {
                set("text", e.target.value);
                setParseHint(false);
              }}
              sx={{ flex: 1 }}
              multiline
              helperText={parseHint ? t("detection.parseText.nothingFound") : " "}
            />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Button
                onClick={fillFromText}
                disabled={d.text.trim() === ""}
                sx={{ mt: 1, whiteSpace: "nowrap" }}
              >
                {t("detection.parseText.action")}
              </Button>
              {allowTextFromFields && (
                <Button
                  onClick={fillTextFromFields}
                  // A row with no code says nothing worth writing down.
                  disabled={d.main_Code.trim() === ""}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {t("detection.textFromFields.action")}
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* The reviewer's verdict on this one detection. Kept visually apart from the coded
            observation above because it is a judgement, not something the AI or OCR read off
            the video — and it is stored separately for the same reason. */}
        <Divider sx={{ mt: 3, mb: 1.5 }} />
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          {t("detection.assessment.title")}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
          <TextField
            select
            label={t("detection.assessment.status")}
            value={d.detectionStatus}
            onChange={(e) => set("detectionStatus", e.target.value)}
          >
            {DETECTION_STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {t(o.labelKey)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label={t("detection.assessment.condition")}
            value={d.conditionRating}
            onChange={(e) => set("conditionRating", e.target.value)}
          >
            {/* Explicit "not assessed" entry: without one there is no way back to unset once
                a rating has been picked by mistake. */}
            <MenuItem value="">
              <em>{t("detection.assessment.notSet")}</em>
            </MenuItem>
            {CONDITION_RATING_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {t(o.labelKey)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label={t("detection.assessment.risk")}
            value={d.riskRating}
            onChange={(e) => set("riskRating", e.target.value)}
          >
            <MenuItem value="">
              <em>{t("detection.assessment.notSet")}</em>
            </MenuItem>
            {RISK_RATING_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {t(o.labelKey)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" disabled={!valid} onClick={() => onSave(d)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetectionEditDialog;
